import { state } from '../shared/state';
import { $, showToast } from '../shared/index';

const STATUS_LABELS: Record<string, string> = {
  running: 'Выполняется',
  completed: 'Завершён',
  failed: 'Ошибка',
};

let kbPollTimer: ReturnType<typeof setTimeout> | null = null;

function authHeaders(): Record<string, string> {
  return { Authorization: 'Bearer ' + state.authToken! };
}

function escapeHtml(str: string): string {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatDate(iso: string | undefined | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ru-RU');
  } catch {
    return iso;
  }
}

function stopKbPolling(): void {
  if (kbPollTimer) {
    clearTimeout(kbPollTimer);
    kbPollTimer = null;
  }
}

function scheduleKbPoll(running: boolean): void {
  stopKbPolling();
  if (running) {
    kbPollTimer = setTimeout(() => void loadAdminKb(), 3000);
  }
}

function renderHealth(data: {
  namespace?: string;
  allowedDir?: string;
  documents?: { total: number; ready: number; failed: number; pending: number; processing: number };
  chunks?: { global: number };
  lastReindex?: { generatedAt?: string; totalChunks?: number; totalBooks?: number; failedBooks?: number } | null;
  failedDocuments?: Array<{ id: string; filename: string; updatedAt: string }>;
  staleFailedCount?: number;
  duplicateReadyCount?: number;
  duplicateGroups?: Array<{ filename: string; keeperDocId: string; duplicateCount: number }>;
}): void {
  const docs = data.documents;
  if ($('kb-stat-chunks')) {
    $('kb-stat-chunks')!.textContent = String(data.chunks?.global ?? 0);
  }
  if ($('kb-stat-docs-ready')) {
    $('kb-stat-docs-ready')!.textContent = docs ? String(docs.ready) : '—';
  }
  if ($('kb-stat-docs-failed')) {
    $('kb-stat-docs-failed')!.textContent = docs ? String(docs.failed) : '—';
  }
  const dupCount = data.duplicateReadyCount ?? 0;
  if ($('kb-duplicate-hint')) {
    $('kb-duplicate-hint')!.textContent = dupCount > 0
      ? `дубликатов ready: ${dupCount}`
      : '';
  }
  if ($('kb-stat-last-reindex')) {
    const last = data.lastReindex;
    $('kb-stat-last-reindex')!.textContent = last?.generatedAt
      ? formatDate(String(last.generatedAt))
      : '—';
  }
  if ($('kb-stat-namespace')) {
    $('kb-stat-namespace')!.textContent = data.namespace || '—';
  }
  if ($('kb-stat-allowed-dir')) {
    $('kb-stat-allowed-dir')!.textContent = data.allowedDir || '—';
  }

  const failedList = $('kb-failed-docs-list');
  if (failedList) {
    const failed = data.failedDocuments || [];
    const stale = data.staleFailedCount ?? 0;
    if (failed.length === 0) {
      const staleNote = stale > 0
        ? `<div class="system-info-item"><span class="system-info-label">Скрыто устаревших сирот: ${stale}</span></div>`
        : '';
      failedList.innerHTML = `${staleNote}<div class="system-info-item"><span class="system-info-label">Нет failed-документов, требующих действий</span></div>`;
    } else {
      failedList.innerHTML = failed.map((doc) => `
        <div class="system-info-item">
          <span class="system-info-label">${escapeHtml(doc.filename)}</span>
          <span class="system-info-value">${formatDate(doc.updatedAt)}</span>
        </div>
      `).join('');
    }
  }
}

function renderReindexStatus(data: {
  job?: { id: string; status: string; startedAt: string; finishedAt?: string; exitCode?: number; error?: string } | null;
  logTail?: string[];
  report?: { generatedAt?: string; totalChunks?: number; totalBooks?: number } | null;
}): void {
  const job = data.job;
  const statusEl = $('kb-reindex-status');
  const logEl = $('kb-reindex-log');
  const reportEl = $('kb-reindex-report');

  if (statusEl) {
    if (!job) {
      statusEl.textContent = 'Нет активного задания';
    } else {
      const label = STATUS_LABELS[job.status] || job.status;
      const finished = job.finishedAt ? ` · ${formatDate(job.finishedAt)}` : '';
      const err = job.error ? ` · ${job.error}` : '';
      statusEl.textContent = `${label} (id ${job.id.slice(0, 8)}…)${finished}${err}`;
    }
  }

  if (logEl) {
    const lines = data.logTail || [];
    logEl.textContent = lines.length > 0 ? lines.join('\n') : 'Лог пуст';
  }

  if (reportEl) {
    const report = data.report;
    if (!report) {
      reportEl.textContent = 'Отчёт reindex ещё не создан';
    } else {
      reportEl.textContent = [
        `generatedAt: ${formatDate(String(report.generatedAt || ''))}`,
        `totalBooks: ${report.totalBooks ?? '—'}`,
        `totalChunks: ${report.totalChunks ?? '—'}`,
      ].join(' · ');
    }
  }

  scheduleKbPoll(job?.status === 'running');
}

export async function loadAdminKb(): Promise<void> {
  if (!state.authToken) return;
  try {
    const headers = authHeaders();
    const [healthRes, statusRes] = await Promise.all([
      fetch('/api/admin/kb/health', { headers, cache: 'no-store' }),
      fetch('/api/admin/kb/reindex/status', { headers, cache: 'no-store' }),
    ]);

    if (healthRes.ok) {
      renderHealth(await healthRes.json());
    }
    if (statusRes.ok) {
      renderReindexStatus(await statusRes.json());
    }
    await loadCorpusOptions();
  } catch (err) {
    console.error('Failed to load admin KB panel', err);
  }
}

/** Список корпусов для формы загрузки: документ должен попадать туда, куда решил куратор. */
async function loadCorpusOptions(): Promise<void> {
  const picker = $<HTMLSelectElement>('kb-upload-store');
  if (!picker) return;
  try {
    const res = await fetch('/api/admin/vector-stores', { headers: authHeaders() });
    if (!res.ok) return;
    const data = await res.json() as {
      items: Array<{ id: string; name: string; mode: string; is_general: boolean }>;
      general_store_id: string;
    };
    const local = data.items.filter((store) => store.mode === 'local');
    picker.innerHTML = local
      .map((store) => `<option value="${store.id}"${store.is_general ? ' selected' : ''}>${escapeHtml(store.name)}</option>`)
      .join('');
  } catch {
    // Каталог недоступен — форма остаётся с общим фондом по умолчанию.
  }
}

async function uploadKbFile(file: File): Promise<void> {
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (!ext || !['md', 'txt', 'markdown'].includes(ext)) {
    showToast('Поддерживаются только .md и .txt', 'error');
    return;
  }

  let content: string;
  try {
    content = await file.text();
  } catch {
    showToast(`Не удалось прочитать «${file.name}»`, 'error');
    return;
  }

  if (!content.trim()) {
    showToast(`Файл «${file.name}» пуст`, 'error');
    return;
  }

  const title = ($<HTMLInputElement>('kb-upload-title')?.value || '').trim() || undefined;
  // Корпус, в который ложится документ (ось корпуса, ADR-005 §3.6). Пусто —
  // общая база знаний: то же поведение, что до появления оси.
  const storeId = ($<HTMLSelectElement>('kb-upload-store')?.value || '').trim() || undefined;

  // Провенанс (Хартия §6): без источника, даты и области применимости документ
  // в корпус не попадает. Форма проверяет это до сети, чтобы куратор не терял
  // выбранный файл на отказе сервера.
  const sourceUri = ($<HTMLInputElement>('kb-upload-source-uri')?.value || '').trim();
  const sourceDate = ($<HTMLInputElement>('kb-upload-source-date')?.value || '').trim();
  const applicability = ($<HTMLInputElement>('kb-upload-applicability')?.value || '').trim();
  const missing = [
    !sourceUri && 'источник',
    !sourceDate && 'дата первоисточника',
    !applicability && 'область применимости',
  ].filter(Boolean) as string[];
  if (missing.length > 0) {
    showToast(`Документ корпуса не принимается без провенанса: ${missing.join(', ')}`, 'error');
    return;
  }

  const res = await fetch('/api/admin/kb/documents/upload', {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({
      filename: file.name,
      content,
      title,
      scope: 'global',
      storeId,
      sourceUri,
      sourceDate,
      applicability,
      docType: 'admin_upload',
    }),
  });

  const data = await res.json().catch(() => ({})) as { detail?: string; chunkCount?: number };
  if (!res.ok) {
    showToast(data.detail || 'Не удалось загрузить документ', 'error');
    return;
  }

  showToast(`Документ проиндексирован (${data.chunkCount ?? '?'} chunks)`, { variant: 'info' });
  await loadAdminKb();
}

async function startReindex(): Promise<void> {
  const volumes = ($<HTMLInputElement>('kb-reindex-volumes')?.value || '').trim();
  const dict = !!$<HTMLInputElement>('kb-reindex-dict')?.checked;
  const filesRaw = ($<HTMLInputElement>('kb-reindex-files')?.value || '').trim();
  const files = filesRaw
    ? filesRaw.split(',').map((f) => f.trim()).filter(Boolean)
    : undefined;

  if (!confirm('Запустить kb:reindex-books? Операция может занять 30–90 минут.')) return;

  const res = await fetch('/api/admin/kb/reindex', {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ volumes: volumes || undefined, dict, files }),
  });

  const data = await res.json().catch(() => ({})) as { detail?: string };
  if (!res.ok) {
    showToast(data.detail || 'Не удалось запустить reindex', 'error');
    return;
  }

  showToast('Reindex запущен', { variant: 'info' });
  await loadAdminKb();
}

export function initAdminKb(): void {
  $('kb-upload-btn')?.addEventListener('click', () => {
    $('kb-upload-file-input')?.click();
  });

  $('kb-upload-file-input')?.addEventListener('change', async (e) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) await uploadKbFile(file);
    input.value = '';
  });

  $('btn-kb-refresh')?.addEventListener('click', () => {
    void loadAdminKb();
  });

  $('btn-kb-start-reindex')?.addEventListener('click', () => {
    void startReindex();
  });

  $('btn-kb-cleanup-duplicates')?.addEventListener('click', () => {
    void cleanupDuplicates();
  });

  $('btn-kb-cleanup-stale')?.addEventListener('click', () => {
    void cleanupStaleFailed();
  });
}

async function cleanupDuplicates(): Promise<void> {
  const previewRes = await fetch('/api/admin/kb/duplicates', { headers: authHeaders() });
  const preview = await previewRes.json().catch(() => ({})) as { duplicateCount?: number };
  const count = preview.duplicateCount ?? 0;
  if (count === 0) {
    showToast('Дубликатов ready не найдено', { variant: 'info' });
    return;
  }
  if (!confirm(`Удалить ${count} дубликат(ов) ready? Для каждого filename останется самый новый doc.`)) return;

  const res = await fetch('/api/admin/kb/cleanup-duplicates', {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ dryRun: false }),
  });
  const data = await res.json().catch(() => ({})) as { detail?: string; deleted?: number; chunksDeleted?: number };
  if (!res.ok) {
    showToast(data.detail || 'Не удалось очистить дубликаты', 'error');
    return;
  }

  showToast(
    `Удалено docs: ${data.deleted ?? 0}, chunks: ${data.chunksDeleted ?? 0}`,
    { variant: 'info' }
  );
  await loadAdminKb();
}

async function cleanupStaleFailed(): Promise<void> {
  if (!confirm('Удалить failed-сироты, для которых уже есть ready-документ с тем же именем?')) return;

  const res = await fetch('/api/admin/kb/cleanup-stale-failed', {
    method: 'POST',
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({})) as { detail?: string; deleted?: number };
  if (!res.ok) {
    showToast(data.detail || 'Не удалось очистить', 'error');
    return;
  }

  showToast(`Удалено устаревших failed: ${data.deleted ?? 0}`, { variant: 'info' });
  await loadAdminKb();
}

export function stopAdminKbPolling(): void {
  stopKbPolling();
}
